// SPDX-License-Identifier: GPL-3.0-or-later
// Code-built gate. The upstream gate loads JSON meshes. This one has two posts and a leaf.
// The leaf pivots about the x axis of the item. At angle 0 it stands up. The item frame has
// its origin at the gate centre on the playfield, and the pivot is `height` above it.

import {
  BoxGeometry,
  type BufferGeometry,
  CatmullRomCurve3,
  CylinderGeometry,
  TubeGeometry,
  Vector3,
} from "three";
import type { GateSpec } from "../../shared/contracts.ts";

/** The parts of a gate. */
export interface GateGeometry {
  /** One post. The scene puts one at each end of the leaf. */
  readonly post: BufferGeometry;
  /** The leaf. Its origin is the pivot line. It extends along +y (up). */
  readonly leaf: BufferGeometry;
  /** Height of the pivot above the playfield. */
  readonly pivotHeight: number;
  /** Height of the leaf above the pivot. */
  readonly leafHeight: number;
}

/** Wire gates are a loop of wire. Types 3 and 4 are plates. */
export function isPlateGate(spec: GateSpec): boolean {
  const type = spec.gateType ?? 1;
  return type === 3 || type === 4;
}

/** Build the geometry of a gate. */
export function generateGate(spec: GateSpec): GateGeometry {
  const pivotHeight = Math.max(spec.height, 4);
  const type = spec.gateType ?? 1;
  const leafHeight = type === 4 ? 44 : 30;
  const half = spec.length / 2;

  const post = new CylinderGeometry(1.6, 1.6, pivotHeight + 2, 10);
  post.translate(0, (pivotHeight + 2) / 2, 0);
  post.name = `gate.post-${spec.name}`;

  let leaf: BufferGeometry;
  if (isPlateGate(spec)) {
    leaf = new BoxGeometry(spec.length, leafHeight, 1.4);
    leaf.translate(0, leafHeight / 2, 0);
  } else {
    // A rectangular loop of wire: down the left side, along the top, down the right side.
    const path = new CatmullRomCurve3(
      [
        new Vector3(-half, 0, 0),
        new Vector3(-half, leafHeight * 0.9, 0),
        new Vector3(-half * 0.9, leafHeight, 0),
        new Vector3(half * 0.9, leafHeight, 0),
        new Vector3(half, leafHeight * 0.9, 0),
        new Vector3(half, 0, 0),
      ],
      false,
      "centripetal",
    );
    leaf = new TubeGeometry(path, 32, 1.3, 6, false);
  }
  leaf.name = `gate.leaf-${spec.name}`;
  return { post, leaf, pivotHeight, leafHeight };
}
